import { useMemo } from "react";

type ShapeType = "circle" | "square" | "triangle" | "hexagon" | "ring";

interface Shape {
  id: number;
  type: ShapeType;
  size: number;
  x: number;
  y: number;
  duration: number;
  delay: number;
  direction: "up" | "down" | "left" | "right" | "diagonal";
  rotation: number;
  opacity: number;
}

const seededValue = (seed: number, salt = 0) => {
  const x = Math.sin(seed * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

const ShapeComponent = ({ shape }: { shape: Shape }) => {
  // Alternate between yellow, blue, and green for the World Cup palette
  const palette = [
    "255, 214, 0",
    "46, 194, 255",
    "0, 184, 107",
  ];
  const primaryColor = palette[shape.id % palette.length];

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    left: `${shape.x}%`,
    top: `${shape.y}%`,
    width: `${shape.size}px`,
    height: `${shape.size}px`,
    opacity: shape.opacity,
    animation: `move${shape.direction} ${shape.duration}s linear infinite, rotate${shape.id % 2 === 0 ? "CW" : "CCW"} ${shape.duration * 2}s linear infinite`,
    animationDelay: `${shape.delay}s`,
  };

  switch (shape.type) {
    case "circle":
      return (
        <div
          style={{
            ...baseStyle,
            borderRadius: "50%",
            border: `2px solid rgba(${primaryColor}, 0.3)`,
            boxShadow: `0 0 20px rgba(${primaryColor}, 0.2), inset 0 0 20px rgba(${primaryColor}, 0.1)`,
          }}
        />
      );
    case "ring":
      return (
        <div
          style={{
            ...baseStyle,
            borderRadius: "50%",
            border: `3px solid rgba(${primaryColor}, 0.25)`,
            boxShadow: `0 0 30px rgba(${primaryColor}, 0.15)`,
          }}
        />
      );
    case "square":
      return (
        <div
          style={{
            ...baseStyle,
            border: `2px solid rgba(${primaryColor}, 0.25)`,
            borderRadius: "8px",
            boxShadow: `0 0 25px rgba(${primaryColor}, 0.15)`,
            transform: `rotate(${shape.rotation}deg)`,
          }}
        />
      );
    case "triangle":
      return (
        <div
          style={{
            ...baseStyle,
            width: 0,
            height: 0,
            borderLeft: `${shape.size / 2}px solid transparent`,
            borderRight: `${shape.size / 2}px solid transparent`,
            borderBottom: `${shape.size}px solid rgba(${primaryColor}, 0.2)`,
            filter: `drop-shadow(0 0 15px rgba(${primaryColor}, 0.3))`,
            background: "transparent",
          }}
        />
      );
    case "hexagon":
      return (
        <div
          style={{
            ...baseStyle,
            clipPath:
              "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            border: `2px solid rgba(${primaryColor}, 0.3)`,
            background: `rgba(${primaryColor}, 0.05)`,
            boxShadow: `0 0 30px rgba(${primaryColor}, 0.2)`,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "3px",
              clipPath:
                "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
              border: `1px solid rgba(${primaryColor}, 0.2)`,
            }}
          />
        </div>
      );
    default:
      return null;
  }
};

export const AnimatedBackground: React.FC = () => {
  const shapes = useMemo(() => {
    const types: ShapeType[] = [
      "circle",
      "square",
      "triangle",
      "hexagon",
      "ring",
    ];
    const directions: Shape["direction"][] = [
      "up",
      "down",
      "left",
      "right",
      "diagonal",
    ];

    return Array.from({ length: 25 }).map(
      (_, i): Shape => ({
        id: i,
        type: types[i % types.length],
        size: seededValue(i, 1) * 120 + 60,
        x: seededValue(i, 2) * 100,
        y: seededValue(i, 3) * 100,
        duration: seededValue(i, 4) * 20 + 15,
        delay: seededValue(i, 5) * 10,
        direction: directions[i % directions.length],
        rotation: seededValue(i, 6) * 360,
        opacity: seededValue(i, 7) * 0.4 + 0.2,
      })
    );
  }, []);

  const floatingDots = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => ({
        id: i,
        size: seededValue(i, 8) * 4 + 2,
        x: seededValue(i, 9) * 100,
        y: seededValue(i, 10) * 100,
        duration: seededValue(i, 11) * 10 + 8,
        delay: seededValue(i, 12) * 5,
      })),
    []
  );

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-black max-h-screen max-w-screen">
      {/* Top-left commemoration stripes */}
      <div
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          width: "320px",
          height: "220px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "26px",
            left: "-70px",
            width: "320px",
            height: "18px",
            borderRadius: "999px",
            transform: "rotate(-18deg)",
            background:
              "linear-gradient(90deg, rgba(255, 214, 0, 0) 0%, rgba(255, 214, 0, 0.95) 20%, rgba(255, 214, 0, 0.95) 80%, rgba(255, 214, 0, 0) 100%)",
            boxShadow: "0 0 24px rgba(255, 214, 0, 0.35)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "54px",
            left: "-56px",
            width: "280px",
            height: "14px",
            borderRadius: "999px",
            transform: "rotate(-18deg)",
            background:
              "linear-gradient(90deg, rgba(0, 184, 107, 0) 0%, rgba(0, 184, 107, 0.9) 22%, rgba(0, 184, 107, 0.9) 78%, rgba(0, 184, 107, 0) 100%)",
            boxShadow: "0 0 22px rgba(0, 184, 107, 0.3)",
          }}
        />
      </div>

      {/* Ambient glow spots - yellow with blue/green accents */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full animate-glow-pulse-1"
        style={{
          top: "-10%",
          left: "-5%",
          background:
            "radial-gradient(circle, rgba(255, 214, 0, 0.24) 0%, transparent 62%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute w-[500px] h-[500px] rounded-full animate-glow-pulse-2"
        style={{
          bottom: "-15%",
          right: "-10%",
          background:
            "radial-gradient(circle, rgba(46, 194, 255, 0.14) 0%, transparent 60%)",
          filter: "blur(50px)",
        }}
      />
      {/* Green accent glow */}
      <div
        className="absolute w-[400px] h-[400px] rounded-full animate-glow-pulse-1"
        style={{
          top: "40%",
          right: "20%",
          background:
            "radial-gradient(circle, rgba(0, 184, 107, 0.14) 0%, transparent 60%)",
          filter: "blur(40px)",
          animationDelay: "2s",
        }}
      />

      {/* Moving geometric shapes */}
      {shapes.map((shape) => (
        <ShapeComponent key={shape.id} shape={shape} />
      ))}

      {/* Floating dots - mix of yellow, blue, and green */}
      {floatingDots.map((dot) => {
        const dotPalette = [
          "rgba(255, 214, 0, 0.9)",
          "rgba(46, 194, 255, 0.75)",
          "rgba(0, 184, 107, 0.75)",
        ];
        const selected = dotPalette[dot.id % dotPalette.length];
        return (
          <div
            key={dot.id}
            className="absolute rounded-full"
            style={{
              width: `${dot.size}px`,
              height: `${dot.size}px`,
              left: `${dot.x}%`,
              top: `${dot.y}%`,
              background: selected,
              boxShadow: `0 0 ${dot.size * 3}px ${selected.replace("0.9", "0.45").replace("0.75", "0.4")}`,
              animation: `floatDot ${dot.duration}s ease-in-out infinite`,
              animationDelay: `${dot.delay}s`,
            }}
          />
        );
      })}

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 214, 0, 1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 184, 107, 1) 1px, transparent 1px)
          `,
          backgroundSize: "100px 100px",
        }}
      />

      {/* Connecting lines effect */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <defs>
          <linearGradient
            id="lineGradientYellow"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="rgba(255, 214, 0, 0)" />
            <stop offset="50%" stopColor="rgba(255, 214, 0, 0.55)" />
            <stop offset="100%" stopColor="rgba(255, 214, 0, 0)" />
          </linearGradient>
          <linearGradient
            id="lineGradientBlue"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="rgba(46, 194, 255, 0)" />
            <stop offset="50%" stopColor="rgba(46, 194, 255, 0.35)" />
            <stop offset="100%" stopColor="rgba(46, 194, 255, 0)" />
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1="30%"
          x2="100%"
          y2="70%"
          stroke="url(#lineGradientYellow)"
          strokeWidth="1"
          className="animate-line-move-1"
        />
        <line
          x1="100%"
          y1="20%"
          x2="0"
          y2="80%"
          stroke="url(#lineGradientBlue)"
          strokeWidth="1"
          className="animate-line-move-2"
        />
        <line
          x1="20%"
          y1="0"
          x2="80%"
          y2="100%"
          stroke="url(#lineGradientYellow)"
          strokeWidth="1"
          className="animate-line-move-3"
        />
      </svg>

      {/* Subtle vignette - no harsh edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 120% 100% at 50% 50%, transparent 40%, rgba(0, 0, 0, 0.3) 100%)",
        }}
      />
    </div>
  );
};
